import { useEffect, useMemo } from 'react';

import Analytics from '@web-solutions/module-analytics';
import { t } from '@web-solutions/module-localization';

import {
  PaymentErrorState,
  getPaymentErrorAnalyticsParams,
} from 'core/payment/utils/payment-error-state';

import { Text } from 'src/onboarding/components/typography/Text/Text';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import Button from 'src/onboarding/components/ui/button/Button';
import { ReactComponent as CloseIcon } from 'src/onboarding/assets/images/close.svg';

import './style.scss';

/** Shared with the callers so the success event lands in the same category. */
export const PAYMENT_FAILED_CATEGORY = 'payment_failed_sheet';

const STATES_TKEY = 'core.subscription.error_popup.states';

type Props = {
  visible: boolean;
  /**
   * Raw provider error. The state, the reason text and the analytics code are
   * derived from it — an unknown or missing code still renders the sheet, as
   * state 3 ("temporary problem").
   */
  error?: any;
  paymentSystem?: string;
  /** 'card' | 'applepay' | 'googlepay' — decides whether state 2 is possible. */
  method?: string;
  /** Disables both buttons while a retry is in flight. */
  loading?: boolean;
  /** Merged into every event, e.g. `{ upsaleID }`. */
  analyticsInfo?: Record<string, any>;
  /**
   * The primary button. Where it leads depends on the state and is the caller's
   * business: state 3 retries the saved card in place, states 1 and 4 open the
   * payment window.
   */
  onRetry: (state: PaymentErrorState) => void;
  /** Skip offer — closes the sheet and moves the user on through the flow. */
  onSkip: () => void;
  /** X or a tap on the dimming — closes the sheet, user stays on the offer. */
  onClose: () => void;
};

/**
 * Bottom sheet shown when an upsale charge fails. Same four states and the same
 * copy as the paywall's error popup — only the buttons differ, because an
 * upsale is bought in one click from the screen and there is no payment window
 * open behind the sheet to return to.
 */
const PaymentFailedSheet = ({
  visible,
  error,
  paymentSystem,
  method,
  loading = false,
  analyticsInfo,
  onRetry,
  onSkip,
  onClose,
}: Props) => {
  const params = useMemo(
    () => getPaymentErrorAnalyticsParams(error, { paymentSystem, method, place: 'upsale' }),
    [error, paymentSystem, method],
  );
  // State 2 can't happen here — the user never typed a card — but the resolver
  // already folds card-data codes into state 1 for wallet methods, and a
  // one-click charge has no card entry either.
  const state =
    params.error_state === PaymentErrorState.CARD_DATA
      ? PaymentErrorState.BANK_DECLINED
      : params.error_state;

  useEffect(() => {
    if (!visible) {
      return;
    }

    Analytics.trackEvent(PAYMENT_FAILED_CATEGORY, 'open', {
      ...analyticsInfo,
      ...params,
      error_state: state,
    });
    // Only the transition into a shown sheet is an impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, state]);

  if (!visible) {
    return null;
  }

  const trackClick = (action: string) => {
    Analytics.trackEvent(PAYMENT_FAILED_CATEGORY, action, {
      ...analyticsInfo,
      ...params,
      error_state: state,
    });
  };

  const handleRetry = () => {
    trackClick('retry');
    onRetry(state);
  };

  const handleSkip = () => {
    trackClick('skip');
    onSkip();
  };

  const handleClose = () => {
    trackClick('close');
    onClose();
  };

  return (
    <>
      <div className='payment-failed-overlay' onClick={handleClose} />
      <div className='payment-failed-sheet'>
        <div className='sheet-title'>
          <Title tag='h4'>{t('onboarding.paymentFailedSheet.title')}</Title>
          <div className='close' onClick={handleClose}>
            <CloseIcon />
          </div>
        </div>

        <Text regular size='m' className='reason-text'>
          {t(`${STATES_TKEY}.${state}.text`)}
        </Text>

        <div className='sheet-controls'>
          <Button
            title={t(`${STATES_TKEY}.${state}.button`)}
            onClick={handleRetry}
            disabled={loading}
          />
          <Button
            title={t('onboarding.paymentFailedSheet.skipOffer')}
            type='secondary'
            onClick={handleSkip}
            disabled={loading}
          />
        </div>
      </div>
    </>
  );
};

export default PaymentFailedSheet;
